import { api } from '../plugins/axios'
import { useOpenEndpoint } from '@/store/modules/steps'
import ParameterMissingError from './errors/ParameterMissingError'
import { API_HOST } from '../constants'

let globalParams = null

function _getTestQueryString () {
  const qs = process.env.VUE_APP_SCP_DEV_QUERYSTRING

  // eslint-disable-next-line
  console.log('Using Testing QS', qs)

  return new URLSearchParams(qs)
}

/**
 * Sets up everything (loading params from query string, etc.)
 *
 * @return {Promise<Awaited<any>>}
 */
export async function loadParams () {
  const params = process.env.NODE_ENV === 'development'
    ? _getTestQueryString()
    : new URLSearchParams(location.search)

  // get all params
  globalParams = parseSearchParams(params)

  let hasSessionReceived = false
  if (Object.hasOwnProperty.call(globalParams, 'use_veeva_vault_auth')) {
    hasSessionReceived = await getVaultSessionFromPostMessage()
  }

  if (hasSessionReceived) {
    // this one won't be protected by the OAuth Proxy. Instead we're using
    // our own auth flow
    useOpenEndpoint()
  }

  // checking required param view
  if (!Object.hasOwnProperty.call(globalParams, 'view')) {
    throw new ParameterMissingError('view')
  }

  // checking required param client
  if (!Object.hasOwnProperty.call(globalParams, 'client')) {
    throw new ParameterMissingError('client')
  }

  // specific url client as _ underscores are not valid urls
  const urlClient = Object.hasOwnProperty.call(globalParams, 'urlClient')
    ? globalParams.urlClient.toLowerCase()
    : globalParams.client.toLowerCase()

  // setting up the api defaults
  api.defaults.headers.common['X-SCP-EMBED-VIEW'] = params.get('view')
  api.defaults.headers.common['X-SC2-CLIENT'] = params.get('client')
  api.defaults.baseURL = `${location.protocol}//${urlClient}.${API_HOST}`

  // what do we share with the step?
  const stepParams = JSON.parse(JSON.stringify(globalParams))

  delete stepParams.view
  delete stepParams.client

  return Promise.resolve(
    // without view and client
    stepParams
  )
}

/**
 * Parse Search Params (dot notation) into a nested object.
 *
 * @param searchParams
 * @param excludeKeys
 * @return {{}}
 */
function parseSearchParams (searchParams, excludeKeys = []) {
  const data = {}

  for (const item of searchParams) {
    if (excludeKeys.includes(item[0])) {
      continue
    }

    const keys = item[0].split('.')

    let ctx = data
    for (let i = 0; i < keys.length; i++) {
      // what do we need to set? if we are on the run, we need to create a new
      // object to contain values in the future – if we are on our last iteration
      // we need to set the value instead
      const value = i === keys.length - 1
        ? item[1]
        : {}

      // set it
      ctx[keys[i]] = value

      // our context will be the current object
      ctx = data[keys[i]]
    }
  }

  return data
}

/**
 * Veeva Vault post message for session_id
 *
 * Keeps listening until a message with the expected session_id payload
 * arrives (or the timeout hits). Unrelated messages (browser extensions,
 * SSO iframes, consent banners, Vault-internal traffic) are logged and
 * skipped instead of consuming the listener or throwing.
 *
 * @return {Promise<boolean>}
 */
async function getVaultSessionFromPostMessage () {
  // eslint-disable-next-line
  console.debug('[Veeva Vault Session Auth] Waiting for Veeva Vault session_id...')

  return new Promise((resolve) => {
    // if we don't get a valid message in 1 second, we break the flow here
    let _timeout = window.setTimeout(() => {
      // eslint-disable-next-line
      console.debug('[Veeva Vault Session Auth] Ran into timeout, skipping session auth.')

      // timeout
      window.removeEventListener('message', listener)
      resolve(false)
      _timeout = null
    }, 1000)

    // handling return values
    const listener = (msg) => {
      // payload must be our JSON envelope carrying the session_id –
      // anything else is skipped and we keep waiting
      let payload
      try {
        payload = typeof msg.data === 'string' ? JSON.parse(msg.data) : msg.data
      } catch (e) {
        // eslint-disable-next-line
        console.debug('[Veeva Vault Session Auth] Ignoring message with non-JSON payload.')
        return
      }

      if (
        !payload ||
        payload.message_id !== 'session_id' ||
        !payload.data ||
        !payload.data.session_id
      ) {
        // eslint-disable-next-line
        console.debug('[Veeva Vault Session Auth] Ignoring message with unexpected shape:', payload)
        return
      }

      // this is our message
      // eslint-disable-next-line
      console.debug('[Veeva Vault Session Auth] Received valid session_id message.')

      window.removeEventListener('message', listener)
      window.clearTimeout(_timeout)
      _timeout = null

      globalParams.vault_session_id = payload.data.session_id
      resolve(true)
    }

    // add the listener to wait for the post message
    window.addEventListener('message', listener)

    // send the post message to the parent window that we are ready
    window.parent.postMessage(JSON.stringify({
      message_id: 'ready',
      data: {}
    }), '*')
  })
}

export function getParams (param) {
  if (param) {
    return globalParams[param] || null
  }

  return globalParams
}
